import React from 'react';
import { ServiceExchange, Language } from '@/types';
import { Card, Badge, Button, LocationMapButton } from '@/components/common';
import { getTranslation, getServiceTypesByIds } from '@/constants';

interface ExchangeCardProps {
  exchange: ServiceExchange;
  language: Language;
  currentUserId?: string;
  onAccept?: (exchangeId: string) => void;
  onDecline?: (exchangeId: string) => void;
  onCancel?: (exchangeId: string) => void;
  onComplete?: (exchangeId: string) => void;
  onChat?: (exchangeId: string) => void;
  showActions?: boolean;
}

interface AvailabilitySlot {
  day: string;
  time: string;
}

export const ExchangeCard: React.FC<ExchangeCardProps> = ({
  exchange,
  language,
  currentUserId,
  onAccept,
  onDecline,
  onCancel,
  onComplete,
  onChat,
  showActions = true,
}) => {
  const t = getTranslation(language);
  const serviceTypes = getServiceTypesByIds(exchange.serviceTypes);

  const isProvider = currentUserId === exchange.providerId;
  const otherUser = isProvider
    ? { name: exchange.requesterName, email: exchange.requesterEmail, photo: exchange.requesterPhotoURL, postalCode: exchange.requesterPostalCode }
    : { name: exchange.providerName, email: exchange.providerEmail, photo: exchange.providerPhotoURL, postalCode: exchange.providerPostalCode };

  // Parse and format availability
  const formatAvailability = (): string => {
    if (!exchange.providerAvailability) return '';

    let availability: AvailabilitySlot[];
    try {
      availability = JSON.parse(exchange.providerAvailability);
    } catch {
      return '';
    }

    if (availability.length === 0) return '';

    const dayNames: Record<string, string> = {
      monday: language === 'en' ? 'Mon' : 'Lun',
      tuesday: language === 'en' ? 'Tue' : 'Mar',
      wednesday: language === 'en' ? 'Wed' : 'Mer',
      thursday: language === 'en' ? 'Thu' : 'Jeu',
      friday: language === 'en' ? 'Fri' : 'Ven',
      saturday: language === 'en' ? 'Sat' : 'Sam',
      sunday: language === 'en' ? 'Sun' : 'Dim',
    };

    const timeNames: Record<string, string> = {
      morning: language === 'en' ? 'AM' : 'Matin',
      afternoon: language === 'en' ? 'PM' : 'Après-midi',
      evening: language === 'en' ? 'Eve' : 'Soir',
    };

    // Group by day
    const grouped: Record<string, string[]> = {};
    availability.forEach((slot) => {
      if (!grouped[slot.day]) grouped[slot.day] = [];
      grouped[slot.day].push(timeNames[slot.time] || slot.time);
    });

    return Object.entries(grouped)
      .map(([day, times]) => `${dayNames[day] || day}: ${times.join(', ')}`)
      .join(' | ');
  };

  const getStatusBadge = () => {
    const statusMap = {
      pending: { variant: 'warning' as const, label: language === 'en' ? 'Pending' : 'En attente' },
      accepted: { variant: 'success' as const, label: language === 'en' ? 'Accepted' : 'Accepté' },
      in_progress: { variant: 'info' as const, label: language === 'en' ? 'In Progress' : 'En cours' },
      completed: { variant: 'success' as const, label: language === 'en' ? 'Completed' : 'Terminé' },
      cancelled: { variant: 'danger' as const, label: language === 'en' ? 'Cancelled' : 'Annulé' },
      declined: { variant: 'danger' as const, label: language === 'en' ? 'Declined' : 'Refusé' },
    };

    const status = statusMap[exchange.status];
    return <Badge variant={status.variant}>{status.label}</Badge>;
  };

  return (
    <Card className="h-full">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          {otherUser.photo ? (
            <img
              src={otherUser.photo}
              alt={otherUser.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-semibold">
                {otherUser.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-900">
              {otherUser.name}
            </h3>
            <p className="text-xs text-gray-500">
              {isProvider
                ? language === 'en'
                  ? 'Requester'
                  : 'Demandeur'
                : language === 'en'
                ? 'Provider'
                : 'Fournisseur'}
            </p>
            {otherUser.postalCode && (
              <LocationMapButton
                postalCode={otherUser.postalCode}
                language={language}
              />
            )}
          </div>
        </div>
        {getStatusBadge()}
      </div>

      <div className="space-y-3">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">Services</p>
          <div className="flex flex-wrap gap-1">
            {serviceTypes.map((st) => (
              <span
                key={st.id}
                className="inline-flex items-center text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded"
              >
                {st.icon} {st.name[language]}
              </span>
            ))}
          </div>
        </div>

        {exchange.description && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">
              {language === 'en' ? 'Details' : 'Détails'}
            </p>
            <p className="text-sm text-gray-700">{exchange.description}</p>
          </div>
        )}

        {/* Show availability if available, otherwise show request date */}
        {formatAvailability() ? (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">
              {t.availability}
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              {formatAvailability()}
            </p>
          </div>
        ) : (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">
              {language === 'en' ? 'Requested' : 'Demandé'}
            </p>
            <p className="text-sm text-gray-700">
              {new Date(exchange.requestedAt).toLocaleDateString(
                language === 'en' ? 'en-US' : 'fr-FR',
                {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }
              )}
            </p>
          </div>
        )}
      </div>

      {showActions && (
        <div className="mt-4 pt-4 border-t space-y-2">
          {exchange.status === 'pending' && isProvider && (
            <div className="flex space-x-2">
              <Button
                variant="success"
                fullWidth
                onClick={() => onAccept && onAccept(exchange.id)}
              >
                {t.accept}
              </Button>
              <Button
                variant="danger"
                fullWidth
                onClick={() => onDecline && onDecline(exchange.id)}
              >
                {t.decline}
              </Button>
            </div>
          )}

          {(exchange.status === 'accepted' || exchange.status === 'in_progress') && (
            <>
              <Button
                variant="primary"
                fullWidth
                onClick={() => onChat && onChat(exchange.id)}
              >
                {t.chat}
              </Button>
              <Button
                variant="success"
                fullWidth
                onClick={() => onComplete && onComplete(exchange.id)}
              >
                {language === 'en' ? 'Mark Complete' : 'Marquer terminé'}
              </Button>
              <Button
                variant="danger"
                fullWidth
                onClick={() => onCancel && onCancel(exchange.id)}
              >
                {t.cancel}
              </Button>
            </>
          )}
        </div>
      )}
    </Card>
  );
};
